import { Add, ContentCopy, Delete, Remove } from "@mui/icons-material"
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { DataNode, NodeDataType, TableCell as TableCellData } from "graphql/types"
import { useEffect } from "react"
import { v4 } from "uuid"

type EditableTableProps = {
  table: DataNode
  setTable: (arg: DataNode) => void
  editMode: boolean
}

/**
 * This function adjusts the colSpan value in a row (header or body)
 * of the cell at position cellIndex. The total number of columns
 * the value from the sum of all colSpan values in the row.
 *
 * Increase (merge): The number of cells to the right of the target cell is removed,
 * that the desired increase is realised.
 *
 * Reduction (Split): The target cell is reduced in size and new standard cells are inserted directly to the right
 * standard cells (colSpan: 1) are inserted directly to the right so that the total number of columns remains the same.
 */

const updateRowColSpan = (
  row: TableCellData[],
  cellIndex: number,
  newSpan: number
): TableCellData[] => {
  const totalCols = row.reduce((sum, cell) => sum + cell.colSpan, 0)
  const startIndex = row.slice(0, cellIndex).reduce((sum, cell) => sum + cell.colSpan, 0)
  const allowedMax = totalCols - startIndex
  const clampedSpan = Math.max(1, Math.min(newSpan, allowedMax))
  const currentSpan = row[cellIndex].colSpan
  if (clampedSpan === currentSpan) {
    return row
  }
  if (clampedSpan > currentSpan) {
    let desiredIncrease = clampedSpan - currentSpan
    const newRowCells: TableCellData[] = []
    for (let i = 0; i < cellIndex; i++) {
      newRowCells.push(row[i])
    }
    const newTargetCell = { ...row[cellIndex], colSpan: clampedSpan }
    let i = cellIndex + 1
    while (desiredIncrease > 0 && i < row.length) {
      const cell = row[i]
      if (cell.colSpan <= desiredIncrease) {
        desiredIncrease -= cell.colSpan
        i++
      } else {
        const remainingSpan = cell.colSpan - desiredIncrease
        newRowCells.push({ ...cell, colSpan: remainingSpan })
        desiredIncrease = 0
        i++
      }
    }
    while (i < row.length) {
      newRowCells.push(row[i])
      i++
    }
    newRowCells.splice(cellIndex, 0, newTargetCell)
    return newRowCells
  } else {
    const decreaseAmount = currentSpan - clampedSpan
    const newRow = [...row]
    newRow[cellIndex] = { ...newRow[cellIndex], colSpan: clampedSpan }
    const newCells: TableCellData[] = Array.from({ length: decreaseAmount }, () => ({
      data: "",
      colSpan: 1,
    }))
    newRow.splice(cellIndex + 1, 0, ...newCells)
    return newRow
  }
}

/**
 * Determines the total number of logical columns in a row.
 */
const getTotalLogicalColumns = (row: TableCellData[]): number =>
  row.reduce((sum, cell) => sum + cell.colSpan, 0)

/**
 * Finds the cell that covers the given logical column index.
 */
const findCellByLogicalIndex = (
  row: TableCellData[],
  logicalColIndex: number
): TableCellData | null => {
  let colStart = 0
  for (const cell of row) {
    const colEnd = colStart + cell.colSpan
    if (logicalColIndex >= colStart && logicalColIndex < colEnd) {
      return cell
    }
    colStart = colEnd
  }
  return null
}

/**
 * Finds the index where a cell should be inserted after the given logical column index.
 */
const findCellInsertIndex = (row: TableCellData[], logicalColIndex: number): number => {
  let colStart = 0
  for (let i = 0; i < row.length; i++) {
    const cell = row[i]
    const colEnd = colStart + cell.colSpan
    if (logicalColIndex >= colStart && logicalColIndex < colEnd) {
      return i
    }
    colStart = colEnd
  }
  return row.length - 1
}

/**
 * Determines whether in the given row the logical column logicalColIndex
 * is covered by a cell that has exactly 1 column.
 */
const isRowColumnDeletable = (row: TableCellData[], logicalColIndex: number): boolean => {
  let colStart = 0
  for (const cell of row) {
    const colEnd = colStart + cell.colSpan
    if (logicalColIndex >= colStart && logicalColIndex < colEnd) {
      return cell.colSpan === 1
    }
    colStart = colEnd
  }
  return false
}

/**
 * A column is considered deletable if in all rows (header and body)
 * the logical column is covered by a cell that has exactly 1 column.
 */
const isColumnDeletable = (logicalColIndex: number, table: DataNode): boolean => {
  if (!isRowColumnDeletable(table.tableHeader, logicalColIndex)) return false
  for (const row of table.tableBody) {
    if (!isRowColumnDeletable(row, logicalColIndex)) return false
  }
  return true
}

/**
 * Helper function to delete a logical column from a row.
 * The cell covering the logical column is removed.
 */
const deleteLogicalColumnFromRow = (
  row: TableCellData[],
  logicalColIndex: number
): TableCellData[] => {
  let colStart = 0
  const newRow: TableCellData[] = []
  for (let i = 0; i < row.length; i++) {
    const cell = row[i]
    const colEnd = colStart + cell.colSpan
    // If the logical column is in this cell and cell.colSpan === 1,
    // we skip this cell (delete it)
    if (logicalColIndex >= colStart && logicalColIndex < colEnd) {
      // Only delete if cell.colSpan === 1 (otherwise the column should not be deletable)
    } else {
      newRow.push(cell)
    }
    colStart = colEnd
  }
  return newRow
}

/**
 * Deletes the logical column logicalColIndex in header and body.
 */
const deleteLogicalColumn = (
  logicalColIndex: number,
  table: DataNode,
  // eslint-disable-next-line no-unused-vars
  setTable: (t: DataNode) => void
) => {
  const newHeader = deleteLogicalColumnFromRow(table.tableHeader, logicalColIndex)
  const newBody = table.tableBody.map((row) => deleteLogicalColumnFromRow(row, logicalColIndex))
  setTable({ ...table, tableHeader: newHeader, tableBody: newBody })
}

export const EditableTable = (props: EditableTableProps) => {
  const { table, setTable, editMode } = props

  const defaultTable: DataNode = {
    id: v4(),
    dataType: NodeDataType.TABLE,
    editable: true,
    deletable: true,
    image: [],
    imageCaption: [],
    imageNumber: [],
    text: "",
    tableCaption: "Meine Tabelle",
    tableNumber: 1,
    tableHeader: [
      { data: "", colSpan: 1 },
      { data: "", colSpan: 1 },
      { data: "", colSpan: 1 },
    ],
    tableBody: [
      [
        { data: "", colSpan: 1 },
        { data: "", colSpan: 1 },
        { data: "", colSpan: 1 },
      ],
      [
        { data: "", colSpan: 1 },
        { data: "", colSpan: 1 },
        { data: "", colSpan: 1 },
      ],
      [
        { data: "", colSpan: 1 },
        { data: "", colSpan: 1 },
        { data: "", colSpan: 1 },
      ],
    ],
  }

  useEffect(() => {
    // If no data is present, the default table is set.
    if (table.tableHeader.length === 0 && table.tableBody.length === 0) {
      setTable(defaultTable)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handler for the header cell content
  const handleHeaderChange = (colIndex: number, value: string) => {
    const newHeader = [...table.tableHeader]
    newHeader[colIndex] = { ...newHeader[colIndex], data: value }
    setTable({ ...table, tableHeader: newHeader })
  }

  // Handler for the content of a body cell
  const handleBodyCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const newBody = table.tableBody.map((row, rIdx) => {
      if (rIdx === rowIndex) {
        return row.map((cell, cIdx) => (cIdx === colIndex ? { ...cell, data: value } : cell))
      }
      return row
    })
    setTable({ ...table, tableBody: newBody })
  }

  // Handler for increasing the colSpan in the header
  const handleHeaderColSpanIncrease = (colIndex: number) => {
    const currentSpan = table.tableHeader[colIndex].colSpan
    const newSpan = currentSpan + 1
    const newHeader = updateRowColSpan(table.tableHeader, colIndex, newSpan)
    setTable({ ...table, tableHeader: newHeader })
  }

  // Handler for decreasing the colSpan in the header
  const handleHeaderColSpanDecrease = (colIndex: number) => {
    const currentSpan = table.tableHeader[colIndex].colSpan
    const newSpan = currentSpan - 1
    const newHeader = updateRowColSpan(table.tableHeader, colIndex, newSpan)
    setTable({ ...table, tableHeader: newHeader })
  }

  // Handler for increasing the colSpan in the body
  const handleBodyColSpanIncrease = (rowIndex: number, colIndex: number) => {
    const row = table.tableBody[rowIndex]
    const currentSpan = row[colIndex].colSpan
    const newSpan = currentSpan + 1
    const newRow = updateRowColSpan(row, colIndex, newSpan)
    const newBody = table.tableBody.map((r, idx) => (idx === rowIndex ? newRow : r))
    setTable({ ...table, tableBody: newBody })
  }

  // Handler for decreasing the colSpan in the body
  const handleBodyColSpanDecrease = (rowIndex: number, colIndex: number) => {
    const row = table.tableBody[rowIndex]
    const currentSpan = row[colIndex].colSpan
    const newSpan = currentSpan - 1
    const newRow = updateRowColSpan(row, colIndex, newSpan)
    const newBody = table.tableBody.map((r, idx) => (idx === rowIndex ? newRow : r))
    setTable({ ...table, tableBody: newBody })
  }

  const addColumn = () => {
    const newHeader: TableCellData[] = [...table.tableHeader, { data: "", colSpan: 1 }]
    const newBody = table.tableBody.map((row) => [...row, { data: "", colSpan: 1 }])
    setTable({ ...table, tableHeader: newHeader, tableBody: newBody })
  }

  // Here the old deleteColumn handler is no longer used.
  // Instead, we delete a logical column – if it is deletable.
  const handleDeleteLogicalColumn = (logicalColIndex: number) => {
    if (isColumnDeletable(logicalColIndex, table)) {
      deleteLogicalColumn(logicalColIndex, table, setTable)
    }
  }

  const addRow = () => {
    const newRow = table.tableHeader.map(() => ({
      data: "",
      colSpan: 1,
    }))
    const newBody = [...table.tableBody, newRow]
    setTable({ ...table, tableBody: newBody })
  }

  const deleteRow = (rowIndex: number) => {
    const newBody = table.tableBody.filter((_, idx) => idx !== rowIndex)
    setTable({ ...table, tableBody: newBody })
  }

  const copyRow = (rowIndex: number) => {
    const rowToCopy = table.tableBody[rowIndex]
    // Erstelle eine tiefe Kopie der Zeile
    const copiedRow = rowToCopy.map((cell) => ({ ...cell }))
    // Füge die kopierte Zeile direkt unterhalb der ursprünglichen Zeile ein
    const newBody = [...table.tableBody]
    newBody.splice(rowIndex + 1, 0, copiedRow)
    setTable({ ...table, tableBody: newBody })
  }

  const copyColumn = (logicalColIndex: number) => {
    // Kopiere die Header-Zelle
    const headerCellToCopy = findCellByLogicalIndex(table.tableHeader, logicalColIndex)
    if (!headerCellToCopy) return

    // Erstelle eine tiefe Kopie der Header-Zelle
    const copiedHeaderCell = { ...headerCellToCopy }

    // Finde die Position der ursprünglichen Zelle im Header
    const headerInsertIndex = findCellInsertIndex(table.tableHeader, logicalColIndex)

    // Füge die kopierte Header-Zelle rechts von der ursprünglichen ein
    const newHeader = [...table.tableHeader]
    newHeader.splice(headerInsertIndex + 1, 0, copiedHeaderCell)

    // Kopiere die entsprechenden Zellen aus allen Body-Zeilen
    const newBody = table.tableBody.map((row) => {
      const cellToCopy = findCellByLogicalIndex(row, logicalColIndex)
      if (!cellToCopy) return row

      const copiedCell = { ...cellToCopy }
      const insertIndex = findCellInsertIndex(row, logicalColIndex)
      const newRow = [...row]
      newRow.splice(insertIndex + 1, 0, copiedCell)
      return newRow
    })

    setTable({ ...table, tableHeader: newHeader, tableBody: newBody })
  }

  // Determine the total number of logical columns (header)
  const totalLogicalColumns = getTotalLogicalColumns(table.tableHeader)

  return (
    <Box>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Tabellenname"
        value={table.tableCaption}
        onChange={(e) => setTable({ ...table, tableCaption: e.target.value })}
        sx={{ mb: 2 }}
      />
      {/* Container: Table on the left, "Add Column" button on the right */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Table>
          <TableHead>
            {/* First row: Copy and Delete buttons for each logical column */}
            {editMode && (
              <TableRow>
                {Array.from({ length: totalLogicalColumns }, (_, logicalColIndex) => (
                  <TableCell
                    key={`actions-${logicalColIndex}`}
                    align="center"
                    sx={{ border: "none", p: 0 }}
                  >
                    <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
                      <Tooltip title="Spalte kopieren">
                        <StyledButton
                          customIcon={<ContentCopy fontSize="small" />}
                          iconOnly={true}
                          onClick={() => copyColumn(logicalColIndex)}
                          size="small"
                          sx={{ mr: 0.5 }}
                        />
                      </Tooltip>
                      <Tooltip title="Spalte löschen">
                        <StyledButton
                          buttonType="delete"
                          iconOnly={true}
                          onClick={() => handleDeleteLogicalColumn(logicalColIndex)}
                          disabled={!isColumnDeletable(logicalColIndex, table)}
                          size="small"
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                ))}
                <TableCell sx={{ border: "none", p: 0 }} />
              </TableRow>
            )}
            {/* Second row: Header cells with text field and colSpan control */}
            <TableRow>
              {table.tableHeader.map((headerCell, colIndex) => (
                <TableCell
                  key={colIndex}
                  colSpan={headerCell.colSpan}
                  sx={{ p: 0, pb: 1, border: "none" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl variant="outlined" fullWidth>
                      <OutlinedInput
                        sx={{ pr: 0, borderRadius: "0px" }}
                        placeholder={`Überschrift ${colIndex + 1}`}
                        value={headerCell.data}
                        onChange={(e) => handleHeaderChange(colIndex, e.target.value)}
                        endAdornment={
                          editMode && (
                            <InputAdornment position="end">
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Button
                                  sx={{
                                    width: "20px",
                                    height: "20px",
                                    minWidth: 0,
                                    mb: 0.5,
                                    p: 0,
                                    borderRadius: "0px",
                                  }}
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleHeaderColSpanIncrease(colIndex)}
                                >
                                  <Add />
                                </Button>
                                <Button
                                  sx={{
                                    width: "20px",
                                    height: "20px",
                                    minWidth: 0,
                                    borderRadius: "0px",
                                  }}
                                  variant="outlined"
                                  onClick={() => handleHeaderColSpanDecrease(colIndex)}
                                  size="small"
                                >
                                  <Remove />
                                </Button>
                              </Box>
                            </InputAdornment>
                          )
                        }
                      />
                    </FormControl>
                  </Box>
                </TableCell>
              ))}
              <TableCell sx={{ border: "none", p: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {table.tableBody.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <TableCell key={colIndex} colSpan={cell.colSpan} sx={{ p: 0, border: "none" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormControl variant="outlined" fullWidth>
                        <OutlinedInput
                          sx={{ pr: 0, borderRadius: "0px" }}
                          fullWidth
                          placeholder={`Inhalt ${rowIndex + 1}-${colIndex + 1}`}
                          value={cell.data}
                          onChange={(e) => handleBodyCellChange(rowIndex, colIndex, e.target.value)}
                          endAdornment={
                            editMode && (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Button
                                    sx={{
                                      width: "20px",
                                      height: "20px",
                                      minWidth: 0,
                                      mb: 0.5,
                                      p: 0,
                                      borderRadius: "0px",
                                    }}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleBodyColSpanIncrease(rowIndex, colIndex)}
                                  >
                                    <Add />
                                  </Button>
                                  <Button
                                    sx={{
                                      width: "20px",
                                      height: "20px",
                                      minWidth: 0,
                                      borderRadius: "0px",
                                    }}
                                    variant="outlined"
                                    onClick={() => handleBodyColSpanDecrease(rowIndex, colIndex)}
                                    size="small"
                                  >
                                    <Remove />
                                  </Button>
                                </Box>
                              </InputAdornment>
                            )
                          }
                        />
                      </FormControl>
                    </Box>
                  </TableCell>
                ))}
                {editMode && (
                  <TableCell sx={{ border: "none", p: 0 }}>
                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="Zeile kopieren">
                        <IconButton onClick={() => copyRow(rowIndex)} size="small">
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Zeile löschen">
                        <IconButton onClick={() => deleteRow(rowIndex)} size="small">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* "Add Column" button to the right of the table */}
        {editMode && (
          <Tooltip title="Spalte hinzufügen">
            <StyledButton buttonType="add" iconOnly={true} onClick={addColumn} sx={{ mt: 4 }} />
          </Tooltip>
        )}
      </Box>
      {/* "Add Row" button below the table */}
      {editMode && (
        <Box
          sx={{
            width: "100%",
            m: 2,
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          <Tooltip title="Zeile hinzufügen">
            <StyledButton buttonType="add" iconOnly={true} onClick={addRow} sx={{ mr: 12 }} />
          </Tooltip>
        </Box>
      )}
      <Typography variant="body2" sx={{ mt: 2 }}>
        Um <b>Fett</b> und <i>Kursiv</i> zu verwenden, umschließen Sie den Text mit ** (fett) oder *
        (kursiv).
      </Typography>
    </Box>
  )
}

export default EditableTable
